import { Link } from "react-router-dom";
import { ArrowRight, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BACKEND_URL } from "@/lib/api";
import { FieldLabel, Panel, SCHEMAS, TagInput } from "@/admin/ui";
import { ImageField } from "@/admin/ImageField";

export const Text = ({ f, set, k, label, hint, placeholder, max, area, rows = 3 }) => {
  const value = f[k] ?? "";
  const Comp = area ? Textarea : Input;
  return (
    <div>
      <FieldLabel hint={max ? `${value.length}/${max}` : hint} warn={!!max && value.length > max}>{label}</FieldLabel>
      <Comp rows={area ? rows : undefined} value={value} onChange={(e) => set({ [k]: e.target.value })} placeholder={placeholder} data-testid={`seo-${k}-input`} />
    </div>
  );
};

export const Num = ({ f, set, k, label, min, max }) => {
  const clamp = () => {
    const n = Number(f[k]);
    set({ [k]: f[k] === "" || Number.isNaN(n) ? min : Math.min(max, Math.max(min, Math.round(n))) });
  };
  return (
    <div>
      <FieldLabel hint={`${min} – ${max}`}>{label}</FieldLabel>
      <Input type="number" min={min} max={max} value={f[k]} onBlur={clamp} onChange={(e) => set({ [k]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`seo-${k}-input`} />
    </div>
  );
};

export const Toggle = ({ f, set, k, label, text, danger }) => (
  <label className={`flex items-start justify-between gap-4 border p-3 ${danger && f[k] ? "border-destructive bg-destructive/5" : "border-border"}`}>
    <span>
      <span className="text-sm font-medium block">{label}</span>
      {text && <span className="text-xs text-muted-foreground">{text}</span>}
    </span>
    <Switch checked={!!f[k]} onCheckedChange={(v) => set({ [k]: v })} data-testid={`seo-${k}-switch`} />
  </label>
);

const Choice = ({ f, set, k, label, options }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select value={String(f[k])} onValueChange={(v) => set({ [k]: v })}>
      <SelectTrigger data-testid={`seo-${k}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const LinkRow = ({ label, path }) => {
  const url = `${BACKEND_URL}${path}`;
  return (
    <div className="flex items-center gap-2 py-2.5 border-b border-border last:border-0">
      <div className="min-w-0 flex-1"><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground truncate">{url}</p></div>
      <Button size="icon" variant="ghost" asChild><a href={url} target="_blank" rel="noopener noreferrer" aria-label="Abrir" data-testid={`seo-open-${path.split("/").pop()}`}><ExternalLink /></a></Button>
      <Button size="icon" variant="ghost" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Copiado"))} aria-label="Copiar" data-testid={`seo-copy-${path.split("/").pop()}`}><Copy /></Button>
    </div>
  );
};

export const GeneralTab = ({ f, set, site }) => {
  const sample = (f.title_template || "").replace("{titulo}", "Iberdrola dispara su beneficio").replace("{sitio}", site);
  return (
    <div className="grid xl:grid-cols-2 gap-6">
      <Panel title="Títulos y descripciones" description="Lo que aparece en la pestaña del navegador y en Google." testid="seo-titles-panel">
        <div className="space-y-4">
          <Text f={f} set={set} k="title_template" label="Plantilla del título de cada página" hint="usa {titulo} y {sitio}" />
          <p className="text-xs text-muted-foreground -mt-2">Ejemplo: <span className="font-mono" data-testid="seo-template-preview">{sample}</span></p>
          <Link to="/admin/paginas" className="flex items-center justify-between gap-3 border-2 border-foreground px-4 py-3 text-sm font-semibold hover:bg-foreground hover:text-background transition-colors" data-testid="seo-open-pages-link">
            <span>El título y la descripción de la portada y de cada página se editan en Páginas</span><ArrowRight className="h-4 w-4 shrink-0" />
          </Link>
        </div>
      </Panel>
      <div className="space-y-6">
        <Panel title="Redes sociales" description="Cómo se ve tu web al compartirla en WhatsApp, LinkedIn o X." testid="seo-social-panel">
          <div className="space-y-4">
            <ImageField label="Imagen por defecto al compartir" hint="1200×630" value={f.default_share_image} onChange={(v) => set({ default_share_image: v })} testid="seo-default-share-image" />
            <Text f={f} set={set} k="twitter_handle" label="Usuario de X (Twitter)" placeholder="@elnoticiero" />
          </div>
        </Panel>
        <Panel title="Indexación" testid="seo-indexing-panel">
          <div className="space-y-3">
            <Toggle f={f} set={set} k="noindex_site" danger label="Ocultar toda la web a los buscadores" text="Solo para cuando estés preparando la web: Google dejará de mostrarla." />
            <Toggle f={f} set={set} k="index_tags" label="Indexar las páginas de temas (#etiquetas)" text="Desactívalo si tienes muchas etiquetas con pocas noticias." />
            <div className="grid sm:grid-cols-2 gap-3">
              <Choice f={f} set={set} k="max_image_preview" label="Tamaño de imagen en Google" options={[["large", "Grande (recomendado)"], ["standard", "Estándar"], ["none", "Sin imagen"]]} />
              <Num f={f} set={set} k="max_snippet" label="Largo del fragmento (-1 = libre)" min={-1} max={1000} />
            </div>
            <Choice f={f} set={set} k="default_schema_type" label="Tipo de contenido por defecto" options={SCHEMAS} />
          </div>
        </Panel>
      </div>
    </div>
  );
};

export const OrgTab = ({ f, set }) => (
  <Panel title="Quién está detrás (E-E-A-T)" description="Google y las IA usan estos datos para confiar en tu periódico y citarlo." testid="seo-org-panel">
    <div className="grid lg:grid-cols-2 gap-4">
      <Text f={f} set={set} k="org_name" label="Nombre del medio" />
      <Text f={f} set={set} k="org_legal_name" label="Razón social" placeholder="El Noticiero Media S.L." />
      <Text f={f} set={set} k="org_founding_year" label="Año de fundación" placeholder="2026" />
      <Text f={f} set={set} k="org_email" label="Email de contacto" placeholder="redaccion@…" />
      <Text f={f} set={set} k="org_phone" label="Teléfono" />
      <Text f={f} set={set} k="publishing_principles_url" label="Página de política editorial" placeholder="/sobre-nosotros" />
      <Text f={f} set={set} k="org_city" label="Ciudad" />
      <Text f={f} set={set} k="org_country" label="País (código)" placeholder="ES" />
      <ImageField label="Logotipo" hint="cuadrado, mínimo 112 px" aspect="aspect-square" value={f.org_logo} onChange={(v) => set({ org_logo: v })} testid="seo-org-logo" />
      <div>
        <FieldLabel hint="Enter para añadir">Perfiles oficiales (LinkedIn, X, YouTube…)</FieldLabel>
        <TagInput value={f.same_as || []} onChange={(same_as) => set({ same_as })} placeholder="https://www.linkedin.com/company/…" testid="seo-same-as" />
      </div>
    </div>
  </Panel>
);

const BOTS = {
  GPTBot: "OpenAI · entrenamiento de modelos", "OAI-SearchBot": "Búsqueda de ChatGPT", "ChatGPT-User": "ChatGPT cuando un usuario abre tu web",
  ClaudeBot: "Anthropic · entrenamiento", "Claude-SearchBot": "Búsqueda de Claude", PerplexityBot: "Perplexity", "Google-Extended": "Gemini y la IA de Google",
  "Applebot-Extended": "Apple Intelligence", CCBot: "Common Crawl (datos abiertos)", "meta-externalagent": "Meta AI", Amazonbot: "Amazon / Alexa", Bytespider: "ByteDance / TikTok",
};

export const CrawlTab = ({ f, set }) => (
  <div className="grid xl:grid-cols-2 gap-6">
    <Panel title="Bots de inteligencia artificial" description="Permitirlos ayuda a que ChatGPT, Perplexity o Gemini te citen (GEO)." testid="seo-bots-panel">
      <div className="divide-y divide-border">
        {Object.keys(f.ai_bots || {}).map((bot) => (
          <label key={bot} className="flex items-center justify-between gap-3 py-2.5">
            <span><span className="font-mono text-sm block">{bot}</span><span className="text-xs text-muted-foreground">{BOTS[bot] || "Bot personalizado"}</span></span>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">{f.ai_bots[bot] ? "Permitido" : "Bloqueado"}
              <Switch checked={!!f.ai_bots[bot]} onCheckedChange={(v) => set({ ai_bots: { ...f.ai_bots, [bot]: v } })} data-testid={`seo-bot-${bot}`} />
            </span>
          </label>
        ))}
      </div>
    </Panel>
    <div className="space-y-6">
      <Panel title="robots.txt" description="Se genera solo con tus ajustes. Añade aquí reglas propias si las necesitas." testid="seo-robots-panel">
        <Text f={f} set={set} k="robots_extra" label="Reglas adicionales" area rows={4} placeholder={"User-agent: NombreDelBot\nDisallow: /ruta"} />
        <div className="mt-3"><LinkRow label="Ver robots.txt" path="/api/robots.txt" /></div>
      </Panel>
      <Panel title="Mapas del sitio y RSS" testid="seo-sitemaps-panel">
        <div className="space-y-3">
          <Toggle f={f} set={set} k="sitemap_images" label="Incluir imágenes en el mapa del sitio" />
          <Toggle f={f} set={set} k="news_sitemap" label="Mapa de Google News (últimas 48 h)" />
          <Toggle f={f} set={set} k="rss_full_content" label="RSS con el texto completo" text="Si lo desactivas, el RSS solo muestra la entradilla." />
          <Num f={f} set={set} k="rss_items" label="Noticias en el RSS" min={5} max={100} />
        </div>
      </Panel>
      <Panel title="Verificación y analítica" description="Pega el código o la etiqueta meta completa que te dé cada servicio." testid="seo-verify-panel">
        <div className="grid sm:grid-cols-2 gap-3">
          <Text f={f} set={set} k="verify_google" label="Google Search Console" />
          <Text f={f} set={set} k="verify_bing" label="Bing Webmaster" />
          <Text f={f} set={set} k="verify_yandex" label="Yandex" />
          <Text f={f} set={set} k="verify_pinterest" label="Pinterest" />
          <Text f={f} set={set} k="verify_facebook" label="Facebook (dominio)" />
        </div>
        <Link to="/admin/codigos" className="mt-4 flex items-center justify-between gap-3 border border-border px-4 py-3 text-sm hover:border-foreground transition-colors" data-testid="seo-open-codes-link">
          <span>Google Analytics, Google Ads, Meta y otros códigos se configuran en <strong>Códigos</strong></span><ArrowRight className="h-4 w-4 shrink-0" />
        </Link>
      </Panel>
    </div>
  </div>
);

export const GeoTab = ({ f, set }) => (
  <div className="grid xl:grid-cols-2 gap-6">
    <div className="space-y-6">
      <Panel title="Cómo escribe la IA" description="Reglas SEO/GEO que siguen las automatizaciones y «Generar con IA»." testid="seo-ai-rules-panel">
        <div className="grid grid-cols-2 gap-3">
          <Num f={f} set={set} k="ai_min_words" label="Palabras mínimas" min={200} max={3000} />
          <Num f={f} set={set} k="ai_max_words" label="Palabras máximas" min={300} max={4000} />
          <Num f={f} set={set} k="ai_key_points" label="Nº de claves" min={0} max={8} />
          <Num f={f} set={set} k="ai_faqs" label="Nº de preguntas frecuentes" min={0} max={8} />
        </div>
        <div className="mt-4">
          <Text f={f} set={set} k="ai_seo_instructions" label="Instrucciones SEO/GEO extra" area rows={4}
            placeholder="Ej.: Incluye una cifra concreta en el titular. Responde la pregunta principal en la primera frase. Usa «pymes» en lugar de «PYMEs»." />
        </div>
      </Panel>
      <Panel title="Bloques GEO en cada noticia" description="Contenido pensado para que las IA te citen." testid="seo-blocks-panel">
        <div className="space-y-3">
          <Toggle f={f} set={set} k="show_key_points" label="Mostrar el bloque de claves" />
          <Text f={f} set={set} k="key_points_title" label="Título del bloque de claves" />
          <Toggle f={f} set={set} k="show_faqs" label="Mostrar las preguntas frecuentes" text="También activa los datos estructurados FAQPage." />
          <Text f={f} set={set} k="faq_title" label="Título de las preguntas frecuentes" />
          <Toggle f={f} set={set} k="speakable" label="Marcar titular y claves para asistentes de voz (speakable)" />
        </div>
      </Panel>
    </div>
    <Panel title="llms.txt (guía para las IA)" description="Un resumen de tu periódico que leen ChatGPT, Claude o Perplexity." testid="seo-llms-panel">
      <div className="space-y-4">
        <Text f={f} set={set} k="llms_intro" label="Presentación del periódico para las IA" area rows={6} />
        <Num f={f} set={set} k="llms_items" label="Noticias listadas en llms.txt" min={5} max={200} />
        <Toggle f={f} set={set} k="llms_full_enabled" label="Publicar llms-full.txt con el texto completo" />
        <Num f={f} set={set} k="llms_full_items" label="Noticias en llms-full.txt" min={5} max={100} />
        <div>
          <LinkRow label="llms.txt" path="/api/llms.txt" />
          <LinkRow label="llms-full.txt" path="/api/llms-full.txt" />
          <LinkRow label="Mapa del sitio" path="/api/sitemap.xml" />
          <LinkRow label="Mapa de Google News" path="/api/news-sitemap.xml" />
          <LinkRow label="RSS" path="/api/rss.xml" />
        </div>
      </div>
    </Panel>
  </div>
);
